import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ImageViewerConfig } from '../image-viewer/image-viewer-config.model';
import * as UTIF from 'utif';
import { ImageViewerComponent } from "../image-viewer/image-viewer.component";
import { PRIMENG_MODULES } from '../../primeng-imports';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';

@Component({
  selector: 'app-file-viewer',
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.scss'],
  standalone:true,
  imports: [...PRIMENG_MODULES, ImageViewerComponent,PdfViewerComponent]
})
export class FileViewerComponent implements OnChanges, OnDestroy {

  @Input() fileName: string = '';  // File name with extension
  @Input() fileUrl: string = '';   // Path or URL of the file
  @Input() itemId: string = '';
  @Input() fromTab: string = 'STab';

  pdfSrc: SafeResourceUrl | null = null;
  private pdfBlob: Blob | null = null;
  private svgObjectUrl: string | null = null;
  private svgLoadGeneration = 0;

  fileType: 'image' | 'pdf' = 'image'; // Type of file

  scale: number = 1;
  rotation: number = 0;
  imageTransform: string = 'scale(1) rotate(0deg)'; // Initial transform for the image

  @ViewChild('tiffCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  convertedUrl: string | null = null;
  format: 'png' | 'jpeg' = 'png';
  isImageLoading: boolean = true;
  isPDFLoading: boolean = true;
  imageIndex = 0;
  imageSrc: any = []
  config: ImageViewerConfig = {
    zoomFactor: 0.1,
    allowFullscreen: false,
    btnIcons: {
      zoomIn: 'pi pi-search-plus',
      zoomOut: 'pi pi-search-minus',
      rotateClockwise: 'pi pi-refresh',
      rotateCounterClockwise: 'pi pi-undo',


    },
    btnShow: {

      rotateClockwise: true,
      rotateCounterClockwise: true,
      zoomIn: true,
      zoomOut: true,
      next: false,
      prev: false,
    },
    customBtns: [

      // { name: 'Reset', icon: 'pi pi-sync' },
      { name: 'Download', icon: 'pi pi-download' }
    ],
  };
  showViewer: boolean = true;
  constructor(private sanitizer: DomSanitizer,
    private http: HttpClient) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.isImageLoading = true
    this.isPDFLoading = true
    // Check if fileName or fileUrl has changed
    if (changes['fileName'] || changes['fileUrl']) {
      this.setFileType();  // Re-run file type detection and content loading

    }
  }

  setFileType(): void {
    this.svgLoadGeneration++;
    this.releaseSvgObjectUrl();
    const sourceFileName = this.fileName || this.extractFileNameFromUrl(this.fileUrl);
    const fileExtension = sourceFileName.split(/[?#]/, 1)[0].split('.').pop()?.toLowerCase() || '';

    if (fileExtension === 'svg') {
      this.fileType = 'image';
      void this.loadSvgAsInlineImage(this.fileUrl, this.svgLoadGeneration);
    } else if (fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png' || fileExtension === 'gif') {
      this.fileType = 'image';
      this.onImageLoadNGX()
    } else if (fileExtension === 'pdf') {
      this.fileType = 'pdf';
      // this.loadPdf();
    } else if (fileExtension.toLowerCase() === 'tif' || fileExtension.toLowerCase() === 'tiff') {
      this.fileType = 'image';
      this.convertTifToPng(this.fileUrl)
    }
  }

  async loadSvgAsInlineImage(url: string, generation = this.svgLoadGeneration): Promise<void> {
    this.isImageLoading = true;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`SVG request failed with status ${response.status}`);
      }

      const svgContent = await response.text();
      if (generation !== this.svgLoadGeneration) {
        return;
      }

      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
      this.svgObjectUrl = URL.createObjectURL(svgBlob);
      this.imageSrc = [this.svgObjectUrl];
      this.onImageLoad();
    } catch (error) {
      if (generation !== this.svgLoadGeneration) {
        return;
      }

      console.error('Error loading SVG image:', error);
      this.imageSrc = [url];
      this.onImageLoad();
    }
  }

  ngOnDestroy(): void {
    this.svgLoadGeneration++;
    this.releaseSvgObjectUrl();
  }

  private releaseSvgObjectUrl(): void {
    if (this.svgObjectUrl) {
      URL.revokeObjectURL(this.svgObjectUrl);
      this.svgObjectUrl = null;
    }
  }

  loadPdf(): void {
    this.http.get(this.fileUrl, { responseType: 'blob' }).subscribe({
      next: (data) => {
        this.pdfBlob = new Blob([data], { type: 'application/pdf' });
        let url = window.URL.createObjectURL(this.pdfBlob);
        this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      },
      error: (error) => {
        console.error('Error loading PDF', error);
      }
    });
  }

  // Download PDF function
  downloadPdf(): void {
    this.downloadFile(this.fileUrl);
  }

  async downloadFile(url: string) {
    const fileName = this.getDownloadFileName();

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback for URLs that cannot be fetched due to browser/CORS restrictions.
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private getDownloadFileName(): string {
    const name = (this.fileName || this.extractFileNameFromUrl(this.fileUrl)).trim();
    const currentItemId = (this.itemId || '').trim();

    if (!currentItemId || !name) {
      return name;
    }

    if (name.toLowerCase().includes(currentItemId.toLowerCase())) {
      return name;
    }

    return `${currentItemId}_${name}`;
  }

  private extractFileNameFromUrl(url: string): string {
    try {
      const cleanUrl = url.split('?')[0].split('#')[0];
      const fileName = cleanUrl.split('/').pop() || '';
      return decodeURIComponent(fileName);
    } catch {
      return '';
    }
  }

  onImageLoad() {
    this.isImageLoading = false;
  }
  onImageError() {
    this.isImageLoading = false;
  }
  onPDFLoad() {
    setTimeout(() => {
      this.isPDFLoading = false
    })

  }
  handleEvent(event: any) {
    switch (event.name) {
      case 'Reset':
        this.imageIndex = 0;
        this.showViewer = false;
        setTimeout(() => {
          this.config = { ...this.config }
          this.showViewer = true;
        })
        break;
      case 'Download':
        this.downloadPdf();
        break;

    }
  }
  onImageLoadNGX() {
    if (this.imageSrc.length > 0)
      this.imageSrc = []
    this.imageSrc = [...this.imageSrc, this.fileUrl]

    this.onImageLoad()
  }

  async convertTifToPng(tifUrl: string) {
    try {
      const response = await fetch(tifUrl);
      const arrayBuffer = await response.arrayBuffer();

      const ifds = UTIF.decode(arrayBuffer);
      UTIF.decodeImage(arrayBuffer, ifds[0]);

      const rgba = UTIF.toRGBA8(ifds[0]); // Get RGBA pixels
      const width = ifds[0].width;
      const height = ifds[0].height;

      // Create a canvas and draw the image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      const imageData = ctx!.createImageData(width, height);
      imageData.data.set(rgba);
      ctx!.putImageData(imageData, 0, 0);

      // Convert to PNG
      this.fileUrl = canvas.toDataURL('image/png');
      this.onImageLoadNGX()
    } catch (error) {
      console.error('Error converting TIF to PNG:', error);
    }
  }
}
